import type {
  APIConfig,
  Message,
  ChatContext,
  XAIMessage,
  XAIMessageContent,
  MessageContent,
  ImageMessageContent
} from '../../types/models';

// Helper function to properly format base64 image data
function formatBase64Image(base64Data: string): string {
  // Remove data URL prefix if present
  const data = base64Data.includes('base64,') ? base64Data.split('base64,')[1] : base64Data;
  
  // Add padding if needed
  let paddedData = data;
  while (paddedData.length % 4 > 0) {
    paddedData += '=';
  }
  
  return `data:image/jpeg;base64,${paddedData}`;
}
import { formatSystemMessages } from '../messageFormatters';
import { usageService } from '../../services/UsageService';
import { APIError } from '../error/errorHandler';
import { useStore } from '../../store/useStore';
import { getProviderConfig, getProviderUrl, getProviderEndpoint, getModelInfo } from '../../config/modelsDatabase';

export const XAI_API_KEY_LINK = "https://platform.x.ai/settings/api-keys";
export const XAI_TUTORIAL_LINK = "https://docs.x.ai/getting-started";

// Helper function to estimate token count from text
function estimateTokenCount(text: string): number {
  // Rough estimation: ~4 characters per token on average
  return Math.ceil(text.length / 4);
}

export async function sendXaiMessage(
  message: string | Array<{
    type: 'text' | 'image_url';
    text?: string;
    image_url?: {
      url: string;
      detail?: 'high' | 'low';
    };
  }>,
  config: APIConfig,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[]
): Promise<string> {
  try {
    // Get xAI configuration
    const providerConfig = getProviderConfig('xai');
    if (!providerConfig) {
      throw new Error('xAI provider configuration not found');
    }

    const chatEndpoint = getProviderEndpoint('xai', 'chat');
    if (!chatEndpoint) {
      throw new Error('xAI chat endpoint not found');
    }

    // Get model information
    const model = getModelInfo(modelId);
    if (!model) {
      throw new Error(`Invalid model ID: ${modelId}`);
    }

    // Helper function to get text content
    const getTextContent = (content: MessageContent): string => {
      if (typeof content === 'string') return content;
      return content.find((c: ImageMessageContent) => c.type === 'text')?.text || '';
    };

    // Helper function to format message content for XAI
    const formatXAIContent = (content: MessageContent): XAIMessageContent => {
      // Handle string content (direct image data)
      if (typeof content === 'string') {
        if (content.startsWith('data:image/')) {
          return [
            {
              type: 'image_url' as const,
              image_url: {
                url: formatBase64Image(content),
                detail: 'high' as const
              }
            },
            {
              type: 'text' as const,
              text: "What's in this image?"
            }
          ];
        }
        return content;
      }

      // Handle array content (pre-formatted messages)
      const formattedContent = content.map(item => {
        if (item.type === 'image_url' && item.image_url) {
          return {
            type: 'image_url' as const,
            image_url: {
              url: formatBase64Image(item.image_url.url),
              detail: 'high' as const
            }
          };
        }
        if (item.type === 'text') {
          return {
            type: 'text' as const,
            text: item.text || "What's in this image?"
          };
        }
        return {
          type: 'text' as const,
          text: "What's in this image?"
        };
      });

      // Ensure at least one text item exists
      if (!formattedContent.some(item => item.type === 'text')) {
        formattedContent.push({
          type: 'text' as const,
          text: "What's in this image?"
        });
      }

      return formattedContent;
    };

    // Filter out empty system messages and add a default if none exists
    const systemMessages = formatSystemMessages(context).filter(msg => getTextContent(msg.content).trim() !== '');
    if (systemMessages.length === 0) {
      systemMessages.push({
        role: 'system',
        content: 'You are a helpful AI assistant.',
        id: 'system-default',
        timestamp: Date.now(),
        modelId: 'system'
      });
    }

    const formattedMessages: XAIMessage[] = [
      ...systemMessages.map(msg => ({
        role: msg.role as 'system',
        content: getTextContent(msg.content)
      })),
      ...previousMessages
        .filter(msg => getTextContent(msg.content).trim() !== '') // Filter out empty messages
        .map(msg => ({
          role: msg.role as 'assistant' | 'user',
          content: getTextContent(msg.content)
        }))
    ];

    // Handle message content
    const userMessage: XAIMessage = {
      role: 'user',
      content: formatXAIContent(message)
    };

    formattedMessages.push(userMessage);

    // Log request for debugging
    console.log('Sending request to xAI:', {
      model: modelId,
      messageCount: formattedMessages.length,
      systemMessages: formattedMessages.filter(m => m.role === 'system').length,
      messages: formattedMessages.map(m => ({ role: m.role, contentLength: m.content.length }))
    });

    // Create request body with essential parameters
    const requestBody = {
      model: modelId,
      messages: formattedMessages,
      stream: false
    };

    // Add optional parameters if configured
    if (config.temperature !== undefined) requestBody.temperature = config.temperature;
    if (config.maxTokens !== undefined) requestBody.max_tokens = config.maxTokens;
    else if (model.maxTokens) requestBody.max_tokens = model.maxTokens;
    
    // Log brief info about the request
    console.log('xAI request:', {
      model: modelId,
      messageCount: formattedMessages.length
    });

    const response = await fetch(getProviderUrl('xai', chatEndpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`,
        ...providerConfig.headers
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorMessage = `xAI API Error: ${response.status} ${response.statusText}`;
      
      try {
        // Try to get the response as JSON
        const errorData = await response.json();
        
        // Handle common error scenarios
        if (response.status === 401) {
          errorMessage = "Authentication failed: Please check your xAI API key";
        } else if (response.status === 404) {
          errorMessage = `Model not found: The model "${modelId}" may not exist or be available`;
        } else if (response.status === 429) {
          errorMessage = "Rate limit exceeded: Please try again later";
        } else if (errorData.error) {
          // Extract the error message from the API response
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.error.type) {
            errorMessage = `Error type: ${errorData.error.type}`;
          }
        }
        
        console.error('xAI API Error:', errorData);
      } catch (parseError) {
        // Fall back to text if JSON parsing fails
        const rawText = await response.text().catch(() => '');
        if (rawText) {
          errorMessage = `xAI API Error: ${rawText.substring(0, 100)}${rawText.length > 100 ? '...' : ''}`;
        }
      }
      
      throw new APIError(errorMessage, 'xAI', response.status || 500);
    }

    const data = await response.json();
    
    // Log brief info about the successful response
    console.log('xAI API Response:', {
      id: data.id,
      model: data.model,
      hasUsage: Boolean(data.usage)
    });

    // Validate the response has the expected format
    if (!data.choices?.[0]?.message?.content) {
      console.error('Invalid xAI response format');
      throw new APIError('Invalid response format from xAI API', 'xAI', 500);
    }

    const content = data.choices[0].message.content;

    // Record usage if available in the response
    const { user, topUpValue } = useStore.getState();
    if (!user) {
      throw new Error('User not authenticated');
    }

    if (data.usage) {
      console.log('xAI API Response with usage:', data);
      console.log('Recording xAI usage (from API):', {
        modelId,
        usage: {
          prompt_tokens: data.usage.prompt_tokens,
          completion_tokens: data.usage.completion_tokens,
          total_tokens: data.usage.total_tokens
        }
      });
      await usageService.recordUsage(user.uid, modelId, {
        prompt_tokens: data.usage.prompt_tokens,
        completion_tokens: data.usage.completion_tokens,
        total_tokens: data.usage.total_tokens
      }, topUpValue);
    } else {
      // If usage data is not available, estimate based on text length
      const promptText = formattedMessages.map(m => m.content).join(' ');
      const promptTokens = estimateTokenCount(promptText);
      const completionTokens = estimateTokenCount(content);

      const estimatedUsage = {
        prompt_tokens: promptTokens,
        completion_tokens: completionTokens,
        total_tokens: promptTokens + completionTokens
      };

      console.log('Recording xAI usage (estimated):', {
        modelId,
        usage: estimatedUsage,
        source: 'estimation',
        promptText,
        completionText: content
      });

      await usageService.recordUsage(user.uid, modelId, estimatedUsage, topUpValue);
    }

    return content;
  } catch (error) {
    console.error('xAI API Error:', error);
    
    // Handle specific error cases
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      
      if (errorMessage.includes('api key') || errorMessage.includes('unauthorized')) {
        throw new APIError('Invalid API key. Please check your xAI API key.', 'xAI', 401);
      }
      
      if (errorMessage.includes('model')) {
        throw new APIError('Invalid model ID or model not available.', 'xAI', 400);
      }
      
      if (errorMessage.includes('rate limit') || errorMessage.includes('quota')) {
        throw new APIError('Rate limit exceeded or quota reached.', 'xAI', 429);
      }
    }
    
    // Re-throw API errors
    if (error instanceof APIError) {
      throw error;
    }
    
    // Generic error
    throw new APIError(
      error instanceof Error ? error.message : 'Failed to communicate with xAI API',
      'xAI',
      500
    );
  }
}